import { useState } from 'react';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import useUser from 'hooks/useUser';
import DropdownMenu from '@alltrails/shared/denali/components/DropdownMenu';
import Button from '@alltrails/shared/denali/components/Button';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import Avatar from '@alltrails/shared/denali/components/Avatar';
import QrCode from '@alltrails/shared/icons/QrCode';
import { fullName } from '@alltrails/shared/utils/formatters/textFormatters';
import { DropdownMenuOption } from '@alltrails/shared/types/dropdownMenu';
import { Context } from 'types/Context';
import logWebHeaderHovered from '@alltrails/analytics/events/logWebHeaderHovered';
import WebHeaderSection from '@alltrails/analytics/enums/WebHeaderSection';
import logWebHeaderLoginButtonTapped from '@alltrails/analytics/events/logWebHeaderLoginButtonTapped';
import { wrapUrlSafe } from '@alltrails/shared/utils/languageSupport';
import useExperiments from 'hooks/useExperiments';
import * as styles from './styles/styles.module.scss';
import { HeaderLink, getHeaderLinkOnClickWithAnalytics, getUserLinks, getUserPermissionLinks } from './headerLinks';
import { HeaderBackground } from './types';
import InstallModal from './InstallModal';

type UserCTAProps = {
  background: HeaderBackground;
  context: Context;
};

const UserCTA = ({ background, context }: UserCTAProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const experiments = useExperiments();

  const [isOpen, setIsOpen] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);

  if (!user) {
    return (
      <Button
        linkInfo={{ href: wrapUrlSafe('/login', languageRegionCode) }}
        onClick={() => {
          logWebHeaderLoginButtonTapped();
        }}
        size="sm"
        text={<FormattedMessage defaultMessage="Log in" />}
        testId="header-log-in"
        variant={background === 'transparentInverted' ? 'inverse' : 'primary'}
      />
    );
  }

  const openDropdown = () => {
    logWebHeaderHovered({ web_header_position: 3, web_header_section: WebHeaderSection.MyAccount });
    setIsOpen(true);
  };

  // Wrapping div around the Avatar is needed for the DropdownMenu to set a ref
  const anchor = (
    <div>
      <Avatar onClick={openDropdown} size="sm" testId="header-avatar" userId={user.id} userName={fullName(user)} />
    </div>
  );

  const options: DropdownMenuOption[] = getUserLinks({ user, languageRegionCode, experiments });
  if (user.subscriptionTier === 'plus' || user.subscriptionTier === 'peak') {
    options.splice(
      1,
      0,
      { sectionHeader: null },
      {
        onClick: () => {
          setIsOpen(false);
          setShowInstallModal(true);
        },
        testId: 'header-continue-in-app',
        title: <FormattedMessage defaultMessage="Continue in app" />,
        trailingIcon: { icon: { Component: QrCode, size: 'sm' } }
      },
      { sectionHeader: null }
    );
  }
  options.push(...getUserPermissionLinks(languageRegionCode, context));

  const optionsWithAnalytics = options.map((option: HeaderLink | DropdownMenuOption, index) => {
    if ('webHeaderDropDownLink' in option) {
      const onClick = getHeaderLinkOnClickWithAnalytics(index, option.webHeaderDropDownLink, WebHeaderSection.MyAccount, option.onClick);
      return { ...option, onClick };
    }
    return option;
  });

  return (
    <>
      <DropdownMenu
        anchor={anchor}
        className={styles.dropdownMenu}
        containerMouseHandlers={{ onMouseEnter: openDropdown, onMouseLeave: () => setIsOpen(false) }}
        isOpen={isOpen}
        onCloseRequest={() => setIsOpen(false)}
        options={optionsWithAnalytics}
        testId="header-avatar-dropdown"
      />
      <InstallModal isOpen={showInstallModal} onRequestClose={() => setShowInstallModal(false)} />
    </>
  );
};

export default UserCTA;
